/**
 * Capgo OTA — live web-bundle updates for Capacitor iOS/Android.
 *
 * Uses `onlyDownload` mode: the native plugin downloads bundles on its own
 * (it starts that work at process launch, before the WebView exists) but never
 * activates them. This module is the only thing that calls `next()`.
 *
 * Two paths have to agree, because `downloadComplete` is not retained for a
 * late listener — if JS registers after the native download hits 100%, the
 * event is gone and `pendingBundleId` stays null forever:
 *   1. Listeners (`downloadComplete`, `updateAvailable`) once JS is alive.
 *   2. `list()` after those listeners, to activate a bundle that is already
 *      on disk. The same reconcile runs on foreground so a download that
 *      finished in the background is not stranded either.
 *
 * Nothing here may block the app mount. The plugin's request budget is
 * `responseTimeout + 5` seconds (10 minutes, because the whole bundle download
 * has to fit inside it — see capacitor.config.ts), so the server config and the
 * channel are resolved in a detached promise; main.js awaits initCapgoOta()
 * before `app.mount()` and would otherwise hold the first frame for all of it.
 *
 * Requires one store release with @capgo/capacitor-updater wired in. After that,
 * UI changes ship through Capgo channels (development / production) without
 * App Store / Play review.
 */
import apiClient from '@/api/client'
import { logError } from '@/services/errorLogger'
import { reloadBlockReason } from '@/services/versionReloadGuard'
import { isNative, isPluginAvailable } from './platform'
import { bundlesFromListResult, pickActivatableBundle } from './capgoOtaBundles'

/** @type {string | null} */
let pendingBundleId = null
let initialized = false
let otaEnabled = true
let lastReconcileAt = 0
/** The channel refusal is a dashboard setting, so log it once, not every boot. */
let channelRefusalLogged = false

const RECONCILE_COOLDOWN_MS = 10000

function reloadBlocked() {
  try {
    return !!reloadBlockReason()
  } catch { /* ignore */ }
  return false
}

async function loadOtaConfigFromServer() {
  try {
    const { data } = await apiClient.get('/mobile/config/')
    const flags = data?.feature_flags || {}
    if (flags.ota_updates === false) otaEnabled = false
    const channel = data?.ota?.channel
    return { enabled: flags.ota_updates !== false, channel: channel || null }
  } catch {
    // Anonymous / offline boot — keep compile-time defaults.
    return { enabled: true, channel: null }
  }
}

/**
 * A refused channel assignment is a Capgo dashboard setting, not a bug we can
 * retry away — say which setting, once, instead of repeating a bare failure.
 * @param {string} channel
 * @param {unknown} reason
 */
function logChannelRefusal(channel, reason) {
  const text = String(reason ?? '')
  if (text.includes('self_set_not_allowed') || text.includes('disabled_by_config')) {
    if (channelRefusalLogged) return
    channelRefusalLogged = true
    console.warn(
      `[CapgoOta] Capgo refused to assign channel "${channel}" (${text}). `
      + 'Enable device self-assignment for that channel in the Capgo dashboard, '
      + 'or assign this device there; the app keeps using its current channel.',
    )
    return
  }
  console.warn('[CapgoOta] setChannel failed:', text)
}

/**
 * Point the device at `channel`, but only when that is both needed and allowed.
 *
 * Capgo's own documentation says not to call setChannel() at boot: a channel
 * that does not allow device self-assignment answers
 * `public_channel_self_set_not_allowed` on every single launch, which is what
 * the device logs were full of. Ask what the device is already on first, and
 * respect `allowSet` before asking the backend for anything.
 *
 * @param {object} CapacitorUpdater
 * @param {string|null} channel
 */
async function ensureOtaChannel(CapacitorUpdater, channel) {
  if (!channel) return
  try {
    const current = await CapacitorUpdater.getChannel()
    if (current?.channel === channel) return
    if (current?.allowSet === false) {
      if (!channelRefusalLogged) {
        channelRefusalLogged = true
        console.warn(
          `[CapgoOta] Channel "${channel}" is dashboard-managed (device `
          + `self-assignment off) — staying on "${current?.channel || 'default'}"`,
        )
      }
      return
    }
  } catch (e) {
    // Unknown state: fall through and let setChannel answer.
    console.warn('[CapgoOta] getChannel failed:', e?.message || e)
  }

  try {
    // Refusals arrive as a resolved value with an error field, not only as a throw.
    const res = await CapacitorUpdater.setChannel({ channel })
    if (res?.error || res?.status === 'error') {
      logChannelRefusal(channel, res.error || res.message)
      return
    }
    console.warn('[CapgoOta] Channel:', channel)
  } catch (e) {
    logChannelRefusal(channel, e?.message || e)
  }
}

function rememberBundle(bundle) {
  const id = bundle?.id
  if (!id || id === 'builtin') return
  pendingBundleId = id
}

async function tryQueuePendingUpdate(CapacitorUpdater) {
  if (!otaEnabled || !pendingBundleId) return
  if (reloadBlocked()) {
    console.warn('[CapgoOta] Update downloaded, activation postponed (call or unsaved form)')
    return
  }
  try {
    const id = pendingBundleId
    await CapacitorUpdater.next({ id })
    pendingBundleId = null
    console.warn('[CapgoOta] Update queued — will apply on next background')
  } catch (e) {
    console.warn('[CapgoOta] next() failed:', e?.message || e)
    logError('custom', '[CapgoOta] failed to queue downloaded bundle', {
      component: 'CapgoOta',
      action: 'next_failed',
      extra: { message: e?.message || String(e) },
    })
  }
}

/**
 * Ask the plugin which bundles are already on disk and queue `next()` for the
 * newest one ahead of the running bundle. Independent of login: a broken
 * session is exactly the case that needs the newer web bundle.
 */
async function reconcileDownloadedBundles(CapacitorUpdater) {
  if (!otaEnabled) return
  const now = Date.now()
  if (now - lastReconcileAt < RECONCILE_COOLDOWN_MS) {
    await tryQueuePendingUpdate(CapacitorUpdater)
    return
  }
  lastReconcileAt = now
  try {
    const [listed, current] = await Promise.all([
      CapacitorUpdater.list(),
      CapacitorUpdater.current(),
    ])
    const pick = pickActivatableBundle(
      bundlesFromListResult(listed),
      current?.bundle || null,
    )
    if (pick) {
      rememberBundle(pick)
      console.warn('[CapgoOta] Activating already-downloaded bundle', pick.version || pick.id)
    }
  } catch (e) {
    console.warn('[CapgoOta] list() failed:', e?.message || e)
  }
  await tryQueuePendingUpdate(CapacitorUpdater)
}

function listenForDownloadedBundles(CapacitorUpdater) {
  CapacitorUpdater.addListener('downloadComplete', (event) => {
    const bundle = event?.bundle
    rememberBundle(bundle)
    if (bundle?.id) {
      console.warn('[CapgoOta] Download complete:', bundle.version || bundle.id)
    }
    tryQueuePendingUpdate(CapacitorUpdater)
  }).catch(() => { /* ignore */ })

  // Retained by the plugin until a listener exists, unlike downloadComplete.
  CapacitorUpdater.addListener('updateAvailable', (event) => {
    const bundle = event?.bundle
    rememberBundle(bundle)
    if (bundle?.id) {
      console.warn('[CapgoOta] Update available:', bundle.version || bundle.id)
    }
    tryQueuePendingUpdate(CapacitorUpdater)
    reconcileDownloadedBundles(CapacitorUpdater).catch((e) => {
      console.warn('[CapgoOta] updateAvailable reconcile failed:', e?.message || e)
    })
  }).catch(() => { /* ignore */ })

  CapacitorUpdater.addListener('downloadFailed', (event) => {
    console.warn('[CapgoOta] Download failed:', event?.version || '')
    logError('custom', '[CapgoOta] bundle download failed', {
      component: 'CapgoOta',
      action: 'download_failed',
      extra: { version: event?.version || null },
    })
  }).catch(() => { /* ignore */ })

  CapacitorUpdater.addListener('channelPrivate', (event) => {
    console.warn(
      '[CapgoOta] Channel is private; device cannot self-assign',
      event?.message || event?.error || '',
    )
  }).catch(() => { /* ignore */ })
}

/**
 * Register Capgo listeners and channel. Call once during native boot (before mount).
 *
 * Listeners are attached before any network wait so a download that finishes
 * during `/mobile/config/` is not dropped. `list()` then picks up a bundle that
 * finished before JS existed at all. The awaited part is local only; the server
 * config and channel run detached so the mount never waits on the network.
 */
export async function initCapgoOta() {
  if (!isNative() || initialized) return
  if (import.meta.env.VITE_CAPGO_OTA === 'false') {
    console.warn('[CapgoOta] Disabled (VITE_CAPGO_OTA=false) — using APK embedded bundle only')
    return
  }
  if (!isPluginAvailable('CapacitorUpdater')) {
    console.warn('[CapgoOta] CapacitorUpdater plugin not available — OTA disabled')
    return
  }
  initialized = true

  const { CapacitorUpdater } = await import('@capgo/capacitor-updater')

  listenForDownloadedBundles(CapacitorUpdater)

  // Re-try when a postponed reload guard clears (same cadence as web version check).
  setInterval(() => { tryQueuePendingUpdate(CapacitorUpdater) }, 30000)

  try {
    const { App } = await import('@capacitor/app')
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return
      reconcileDownloadedBundles(CapacitorUpdater).catch((e) => {
        console.warn('[CapgoOta] foreground reconcile failed:', e?.message || e)
      })
    })
  } catch (e) {
    console.warn('[CapgoOta] App lifecycle listener failed:', e?.message || e)
  }

  const compileChannel = import.meta.env.VITE_CAPGO_CHANNEL || 'production'

  // Everything past this point talks to the network, and main.js awaits
  // initCapgoOta() BEFORE app.mount(). The plugin's request budget is
  // responseTimeout + 5s — now 10 minutes, so a whole bundle fits in it — and a
  // dead or slow network would hold the mount for all of it: minutes of white
  // screen before the login form exists. Detached on purpose; nothing about the
  // first frame depends on the channel.
  ;(async () => {
    const remote = await loadOtaConfigFromServer()
    if (!remote.enabled) {
      otaEnabled = false
      console.warn('[CapgoOta] Server disabled OTA (feature_flags.ota_updates=false)')
      return
    }
    await ensureOtaChannel(CapacitorUpdater, remote.channel || compileChannel)
    await reconcileDownloadedBundles(CapacitorUpdater)
  })().catch((e) => {
    console.warn('[CapgoOta] server config failed:', e?.message || e)
  })
}

/**
 * Tell Capgo the current bundle booted successfully (rollback protection).
 * Call after Vue mount on native.
 */
export async function notifyCapgoAppReady() {
  if (!isNative() || !isPluginAvailable('CapacitorUpdater')) return
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    await CapacitorUpdater.notifyAppReady()
    console.warn('[CapgoOta] notifyAppReady OK')
  } catch (e) {
    console.warn('[CapgoOta] notifyAppReady failed:', e?.message || e)
    logError('custom', '[CapgoOta] notifyAppReady failed', {
      component: 'CapgoOta',
      action: 'notify_app_ready_failed',
      extra: { message: e?.message || String(e) },
    })
  }
}

/**
 * The web bundle the app is running right now.
 *
 * A technician who reports "the old screen is still there" is almost always on a
 * bundle that Capgo downloaded but has not activated yet (activation happens on
 * the next background), so the running version has to be visible in the app.
 *
 * `builtin` means the APK/IPA embedded bundle — no OTA has been applied at all.
 * Returns null on web / when the plugin is missing; the caller then falls back
 * to the compile-time build stamp.
 *
 * @returns {Promise<{version: string, downloadedAt: string|null, isBuiltin: boolean, native: string}|null>}
 */
export async function currentBundleInfo() {
  if (!isNative() || !isPluginAvailable('CapacitorUpdater')) return null
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    const res = await CapacitorUpdater.current()
    const bundle = res?.bundle || {}
    const version = String(bundle.version || '')
    // Capgo reports the embedded bundle as "builtin" and stamps it with the
    // epoch, which must not be shown as a real update date.
    const downloaded = String(bundle.downloaded || '')
    const epoch = !downloaded || downloaded.startsWith('1970')
    return {
      version: version || String(res?.native || ''),
      downloadedAt: epoch ? null : downloaded,
      isBuiltin: !version || version === 'builtin',
      native: String(res?.native || ''),
    }
  } catch (e) {
    console.warn('[CapgoOta] current() failed:', e?.message || e)
    return null
  }
}

/**
 * Refresh OTA channel / enable flag after login (server is source of truth),
 * then actively check Capgo for a newer bundle and queue it.
 */
export async function refreshCapgoOtaConfig() {
  if (!isNative() || !initialized || !isPluginAvailable('CapacitorUpdater')) return
  const remote = await loadOtaConfigFromServer()
  otaEnabled = remote.enabled
  if (!remote.enabled) {
    console.warn('[CapgoOta] refresh: OTA disabled by server')
    return
  }
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    await ensureOtaChannel(CapacitorUpdater, remote.channel)
    await reconcileDownloadedBundles(CapacitorUpdater)
    // After login the channel may have just switched (e.g. production →
    // development). Force a check so the device does not wait for periodCheck.
    const latest = await CapacitorUpdater.getLatest(
      remote.channel ? { channel: remote.channel } : undefined,
    )
    if (latest?.kind === 'up_to_date') {
      console.warn('[CapgoOta] refresh: already up to date', latest.version || '')
      return
    }
    if (latest?.kind === 'blocked') {
      console.warn('[CapgoOta] refresh: update blocked', latest.error || latest.message)
      return
    }
    if (!latest?.url && !latest?.manifest) {
      console.warn('[CapgoOta] refresh: no downloadable bundle', latest?.kind || latest?.error)
      return
    }
    console.warn('[CapgoOta] refresh: downloading', latest.version || latest.url)
    const bundle = await CapacitorUpdater.download({
      url: latest.url,
      version: latest.version,
      sessionKey: latest.sessionKey,
      checksum: latest.checksum,
      manifest: latest.manifest,
    })
    if (bundle?.id) {
      rememberBundle(bundle)
      await tryQueuePendingUpdate(CapacitorUpdater)
    }
  } catch (e) {
    console.warn('[CapgoOta] refresh failed:', e?.message || e)
  }
}
